import { useMemo, useState } from 'react';
import { api, ChatChannel, ChatMessage, ChatPerson } from '../api';
import { Avatar } from '../components/Avatar';
import { ChatAvatar } from './ChatAvatar';
import { PanelHead } from '../nav/PanelHead';
import { IconCheck, IconSearch } from '../components/icons';
import { MissingNote } from '../components/MissingNote';

/*
 * Простые окна списка чатов: «написать сотруднику», «создать канал» и «новая
 * папка». Первым двум нужен один и тот же список людей с поиском, третье
 * держится рядом, потому что открывается из той же полосы над списком.
 */

export function PersonRows({
  people,
  query,
  selected,
  locked,
  onPick,
}: {
  people: ChatPerson[];
  query: string;
  selected?: Set<string>;
  /** Выбраны заранее и менять нельзя — например, уже в звонке. */
  locked?: Set<string>;
  onPick: (p: ChatPerson) => void;
}) {
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.login.toLowerCase().includes(q))
      .slice(0, 40);
  }, [people, query]);

  if (!list.length) return <div className="empty">Никого не нашлось</div>;
  return (
    <div className="row-list ch-people">
      {list.map((p) => {
        // уже участвующие в звонке показаны выбранными и недоступными
        const on = !!selected?.has(p.id) || !!locked?.has(p.id);
        return (
          <button
            key={p.id}
            type="button"
            className={`row${on ? ' on' : ''}`}
            aria-pressed={selected ? on : undefined}
            disabled={locked?.has(p.id)}
            onClick={() => onPick(p)}
          >
            <Avatar login={p.login} name={p.name} size={32} />
            <span className="row-text">
              <span className="row-title">{p.name}</span>
            </span>
            {on && (
              <span className="row-meta ibtn sm on" aria-hidden="true">
                <IconCheck />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function NewDmDialog({
  people,
  onClose,
  onOpened,
}: {
  people: ChatPerson[];
  onClose: () => void;
  onOpened: (channelId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const open = (p: ChatPerson) => {
    if (busy) return;
    setBusy(true);
    api
      .chatOpenDm(p.id)
      .then((res) => onOpened(res.id))
      .catch(() => setBusy(false));
  };

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="dialog ch-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <PanelHead title="Написать сотруднику" onBack={onClose} />
        <div className="dialog-body form">
          <label className="search">
            <IconSearch />
            <input autoFocus value={query} placeholder="Имя сотрудника" aria-label="Имя сотрудника" onChange={(e) => setQuery(e.target.value)} />
          </label>
          <PersonRows people={people} query={query} onPick={open} />
        </div>
      </div>
    </div>
  );
}

/*
 * Новая папка. Раньше название вводилось прямо в полосе папок: поле
 * растягивалось на всю ширину полосы, выталкивало пилюли за край и меняло её
 * высоту. Окно с одним полем не трогает полосу вовсе (Илья, 10.09.2026).
 */
export function NewFolderDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  /** Создание живёт в списке чатов: там же лежит состав папок. */
  onCreate: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const nameMissing = !name.trim();

  const create = () => {
    const clean = name.trim();
    if (!clean || busy) return;
    setBusy(true);
    setError('');
    onCreate(clean)
      .then(onClose)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Не удалось создать папку');
        setBusy(false);
      });
  };

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="dialog ch-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <PanelHead title="Новая папка" sub="Свои папки видите только вы." onBack={onClose} />
        <div className="dialog-body form">
          <div className="field">
            <label htmlFor="chf-name">
              Название <i>*</i>
            </label>
            <input
              id="chf-name"
              className={`input${nameMissing ? ' missing' : ''}`}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
            />
            {nameMissing && <MissingNote />}
          </div>
          {error && <div className="banner crit">{error}</div>}
        </div>
        <div className="dialog-foot">
          <button className="btn outline" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={!name.trim() || busy} onClick={create}>
            Создать
          </button>
        </div>
      </div>
    </div>
  );
}

/*
 * Пересылка: отмечаем чаты галочками и отправляем копию сразу во все
 * (решение Ильи 11.09.2026). Чат уведомлений и те, где нельзя писать, в
 * списке не показываются — переслать туда всё равно не выйдет.
 */
export function ForwardDialog({
  channels,
  message,
  onClose,
  onDone,
}: {
  channels: ChatChannel[];
  message: ChatMessage;
  onClose: () => void;
  onDone: (sent: number) => void;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return channels
      .filter((c) => c.kind !== 'system' && c.canPost && !c.archived)
      .filter((c) => !q || c.name.toLowerCase().includes(q));
  }, [channels, query]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const send = () => {
    if (!picked.size || busy) return;
    setBusy(true);
    setError('');
    api
      .chatForward([message.id], [...picked])
      .then((res) => onDone(res.sent))
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Не удалось переслать');
        setBusy(false);
      });
  };

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="dialog ch-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <PanelHead
          title="Переслать"
          sub={message.text ? message.text.slice(0, 80) : 'Сообщение с вложением'}
          onBack={onClose}
        />
        <div className="dialog-body form">
          <label className="search">
            <IconSearch />
            <input
              autoFocus
              value={query}
              placeholder="Название чата"
              aria-label="Название чата"
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className="row-list divided">
            {list.map((c) => (
              <label className="row" key={c.id}>
                <ChatAvatar channel={c} size={32} />
                <span className="row-text">
                  <span className="row-title">{c.name}</span>
                </span>
                <span className="check">
                  <input
                    type="checkbox"
                    checked={picked.has(c.id)}
                    onChange={() => toggle(c.id)}
                    aria-label={`Переслать в «${c.name}»`}
                  />
                </span>
              </label>
            ))}
            {!list.length && <div className="empty">Подходящих чатов нет.</div>}
          </div>
          {error && <div className="banner crit">{error}</div>}
        </div>
        <div className="dialog-foot">
          <button className="btn outline" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={!picked.size || busy} onClick={send}>
            Переслать{picked.size > 1 ? ` в ${picked.size}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NewChannelDialog({
  people,
  kind,
  onClose,
  onCreated,
}: {
  people: ChatPerson[];
  /** Группа — пишут все; канал — публикуют администраторы. */
  kind: 'group' | 'channel';
  onClose: () => void;
  onCreated: (channelId: string) => void;
}) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [isPrivate, setPrivate] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const nameMissing = !name.trim();

  const toggle = (p: ChatPerson) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) next.delete(p.id);
      else next.add(p.id);
      return next;
    });
  };

  const create = () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError('');
    api
      .chatCreateChannel({
        kind,
        name: name.trim(),
        topic: topic.trim(),
        isPrivate,
        memberIds: [...picked],
      })
      .then((res) => onCreated(res.id))
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Не удалось создать чат');
        setBusy(false);
      });
  };

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="dialog ch-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <PanelHead
          title={kind === 'channel' ? 'Новый канал' : 'Новая группа'}
          sub={
            kind === 'channel'
              ? 'Публикуют администраторы канала, отвечают в комментариях все участники.'
              : 'Переписка на всех участников: пишет каждый.'
          }
          onBack={onClose}
        />
        <div className="dialog-body form">
          <div className="field">
            <label htmlFor="chn-name">
              Название <i>*</i>
            </label>
            <input
              id="chn-name"
              className={`input${nameMissing ? ' missing' : ''}`}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {nameMissing && <MissingNote />}
          </div>
          <div className="field">
            <label htmlFor="chn-topic">{kind === 'channel' ? 'О чём канал' : 'О чём группа'}</label>
            <input id="chn-topic" className="input" value={topic} onChange={(e) => setTopic(e.target.value)} />
          </div>
          <label className="check">
            <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} />
            Закрытый: виден только приглашённым
          </label>
          <div className="field">
            <span className="field-label">Кого позвать</span>
            <label className="search md">
              <IconSearch />
              <input value={query} placeholder="Имя или никнейм" aria-label="Имя или никнейм" onChange={(e) => setQuery(e.target.value)} />
            </label>
            <PersonRows people={people} query={query} selected={picked} onPick={toggle} />
          </div>
          {error && <div className="banner crit">{error}</div>}
        </div>
        <div className="dialog-foot">
          <button className="btn outline" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={!name.trim() || busy} onClick={create}>
            Создать
          </button>
        </div>
      </div>
    </div>
  );
}
